<template>
    <div class="announcement-create loading-content-container">
        <div v-if="isLoading" class="loading-overlay d-flex justify-content-center pt-5">
            <csp-loading-indicator/>
        </div>

        <b-row v-if="!isLoading">
            <csp-desktop-info-card
                :cols="12"
                :form-context="initialFormContexts.announcement"
                :show-buttons="false"
                title="Neue Ankündigung"
                @save="saveAnnouncement">
                <b-row>
                    <csp-desktop-info-card-element
                        class="col-12"
                        :form-context="initialFormContexts.announcement"
                        label="Inhalt"
                        property="announcement"
                        required
                        :show-label="false"
                        type="ckeditorReduced"/>
                </b-row>
                <b-row>
                    <csp-desktop-info-card-element
                        class="col-2"
                        :form-context="initialFormContexts.announcement"
                        label="Gültig ab"
                        :max-date="null"
                        property="dateBegin"
                        type="datepicker"/>

                    <csp-desktop-info-card-element
                        class="col-2"
                        :form-context="initialFormContexts.announcement"
                        label="Gültig bis"
                        :max-date="null"
                        property="dateEnd"
                        type="datepicker"/>

                    <csp-desktop-info-card-element
                        class="col-2"
                        :form-context="initialFormContexts.announcement"
                        label="Wie oft anzeigen? (0 = unbegrenzt)"
                        property="maxOccurrence"
                        type="number"/>

                    <csp-desktop-info-card-element
                        class="col-4"
                        :form-context="initialFormContexts.announcement"
                        label="Zielgruppe"
                        property="roleGroup"
                        type="checkboxGroup"/>

                    <csp-desktop-info-card-element
                        class="col-2"
                        :form-context="initialFormContexts.announcement"
                        label="Gebiet"
                        property="area"
                        type="autocomplete"/>
                </b-row>

                <b-row>
                    <csp-desktop-info-card-element
                        class="col-3"
                        :form-context="initialFormContexts.announcement"
                        label="Firma"
                        :options-loader="loadFirmOptions"
                        property="firm"
                        type="autocomplete"/>

                    <csp-desktop-info-card-element
                        class="col-3"
                        :form-context="initialFormContexts.announcement"
                        label="Firmengruppe"
                        :options-loader="loadFirmGroupOptions"
                        property="firmGroup"
                        type="autocomplete"/>

                    <csp-desktop-info-card-element
                        class="col-3"
                        :form-context="initialFormContexts.announcement"
                        label="Zuordnung"
                        :options-loader="loadFirmAssociationOptions"
                        property="firmAssociation"
                        type="autocomplete"/>
                </b-row>

                <b-row class="mt-3">
                    <b-col cols="12">
                        <label class="font-weight-bold">Bild</label>
                        <p class="text-muted mb-2">
                            Platzhalter für die Bildposition im Text:
                            <code v-pre>{{ ankuendigung.bild }}</code>
                            <a
                                v-if="uploadedFileId"
                                class="ml-2"
                                title="Bild entfernen"
                                @click.prevent="removeFile">
                                <i class="inticon-autoclaim_delete" style="font-size: 1.5rem;"/>
                            </a>
                        </p>
                        <b-img v-if="uploadedFileId"
                            class="announcement-image-preview"
                            fluid
                            :src="`/api/desktop/announcements/files/${uploadSessionId}/${uploadedFileId}`"/>
                        <csp-base-file-upload
                            v-if="!uploadedFileId"
                            accepted-mime-types="image/jpeg,image/png"
                            :additional-params="fileUploadParams"
                            :max-files="1"
                            upload-url="/api/desktop/announcements/files"
                            @upload-complete="handleFileUploadComplete"/>
                    </b-col>
                </b-row>

                <template>
                    <b-row class="mt-3">
                        <b-col cols="6"/>
                        <b-col class="text-right" cols="6">
                            <b-button
                                v-if="showPreviewButton"
                                class="mr-2"
                                :disabled="isLoadingPreview"
                                variant="primary"
                                @click="openPreview">
                                Vorschau
                            </b-button>
                            <b-button
                                class="mr-2"
                                variant="secondary"
                                @click="$router.push('/de/Schaden-Manager/Dokument/announcements')">
                                Abbrechen
                            </b-button>
                            <b-button
                                variant="primary"
                                @click="saveAnnouncement(initialFormContexts.announcement)">
                                Speichern
                            </b-button>
                        </b-col>
                    </b-row>
                </template>
            </csp-desktop-info-card>
        </b-row>

        <csp-announcements
            v-if="isPreviewModalOpen"
            acknowledge-url=""
            :announcement-html="previewHtml"
            :announcement-ids="[]"
            :show-confirm="false"
            @hidden="isPreviewModalOpen = false"/>
    </div>
</template>

<script>
    import FormContext from '@desktop/components/FormContext';
    import FormDataMixin from '@desktop/components/mixins/FormDataMixin';
    import Container from '@services/Container';
    import {generateUploadSessionId} from '@js/functions/session';

    const container = Container.getInstance();
    const repository = container.getDesktopAnnouncementRepository();

    export default {
        name: 'AnnouncementCreate',
        mixins: [FormDataMixin],
        data() {
            return {
                isLoading: true,
                isPreviewModalOpen: false,
                isLoadingPreview: false,
                previewHtml: null,
                uploadSessionId: generateUploadSessionId(),
                uploadedFileId: null,
                initialFormContexts: {
                    announcement: new FormContext({
                        name: 'announcement',
                        isAlwaysInEditMode: true,
                    }),
                },
            };
        },
        computed: {
            fileUploadParams() {
                return {uploadSessionId: this.uploadSessionId};
            },
            showPreviewButton() {
                return !!this.initialFormContexts.announcement.form.getData().announcement;
            },
        },
        watch: {
            'initialFormContexts.announcement.form.data.firm'(newValue) {
                if (!newValue) {
                    return;
                }
                // reset other values upon value selection, only one of the three can be set
                this.initialFormContexts.announcement.form.setProperty('firmGroup', null);
                this.initialFormContexts.announcement.form.setProperty('firmAssociation', null);
            },
            'initialFormContexts.announcement.form.data.firmGroup'(newValue) {
                if (!newValue) {
                    return;
                }
                // reset other values upon value selection, only one of the three can be set
                this.initialFormContexts.announcement.form.setProperty('firm', null);
                this.initialFormContexts.announcement.form.setProperty('firmAssociation', null);
            },
            'initialFormContexts.announcement.form.data.firmAssociation'(newValue) {
                if (!newValue) {
                    return;
                }
                // reset other values upon value selection, only one of the three can be set
                this.initialFormContexts.announcement.form.setProperty('firm', null);
                this.initialFormContexts.announcement.form.setProperty('firmGroup', null);
            },
        },
        created() {
            this.loadFormData(() => repository.getBlank())
                .finally(() => {
                    this.isLoading = false;
                });
        },
        methods: {
            handleFileUploadComplete(data) {
                const response = JSON.parse(data.xhr.response);
                this.uploadedFileId = response?.data?.attributes?.fileId ?? null;
            },
            loadFirmAssociationOptions() {
                return repository.loadFirmAssociationOptions();
            },
            loadFirmGroupOptions() {
                return repository.loadFirmGroupOptions();
            },
            loadFirmOptions() {
                return repository.loadFirmOptions();
            },
            openPreview() {
                this.isLoadingPreview = true;
                repository.preview({
                    announcementHtml: this.initialFormContexts.announcement.form.getData().announcement,
                    uploadSessionId: this.uploadSessionId,
                }).then((response) => {
                    this.previewHtml = response.data.attributes.resolvedHtml;
                    this.isPreviewModalOpen = true;
                }).finally(() => {
                    this.isLoadingPreview = false;
                });
            },
            removeFile() {
                this.uploadedFileId = null;
            },
            saveAnnouncement(formContext) {
                this.saveFormData(
                    formContext,
                    (id, postData) => {
                        postData.uploadSessionId = this.uploadSessionId;
                        return repository.create(postData);
                    },
                    false
                ).then(() => {
                    this.$router.push('/de/Schaden-Manager/Dokument/announcements');
                }).catch(() => {
                    // validation errors are already handled by saveFormData
                });
            },
        },
    };
</script>
